import { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { useCookieConsent } from '../hooks/useCookieConsent'
import { trackPageView } from '../lib/analytics'

// Reserves its own height as body padding. revealOnEngage delays it: padding cannot clear hero CTAs under the bar.
export default function CookieConsent({ revealOnEngage = false }: { revealOnEngage?: boolean } = {}) {
  const { consent, accept, decline } = useCookieConsent()
  const barRef = useRef<HTMLDivElement>(null)
  const [engaged, setEngaged] = useState(!revealOnEngage)
  const visible = consent === null && engaged

  useEffect(() => {
    if (engaged) return
    const show = () => setEngaged(true)
    window.addEventListener('scroll', show, { once: true, passive: true })
    // Visitors who never scroll still get the choice.
    const t = window.setTimeout(show, 8000)
    return () => {
      window.removeEventListener('scroll', show)
      window.clearTimeout(t)
    }
  }, [engaged])

  useEffect(() => {
    if (!visible) return
    const el = barRef.current
    if (!el) return

    const body = document.body
    const apply = () => {
      // offsetHeight is 0 in jsdom, hence the fallback.
      const h = el.offsetHeight || el.getBoundingClientRect().height
      body.style.setProperty('--cookie-banner-height', `${h}px`)
      body.style.paddingBottom = 'var(--cookie-banner-height)'
    }
    apply()

    // Copy rewraps on rotate, so the height must be re-measured.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null
    ro?.observe(el)

    return () => {
      ro?.disconnect()
      body.style.removeProperty('--cookie-banner-height')
      body.style.removeProperty('padding-bottom')
    }
  }, [visible])

  if (!visible) return null

  const handleAccept = () => {
    accept()
    // Re-send the pageview now that consent exists, so this session is stitched.
    void trackPageView()
  }

  return (
    <Paper
      ref={barRef}
      elevation={4}
      sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1300 }}
    >
      <Box
        sx={{
          maxWidth: 'lg',
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          py: { xs: 1.25, sm: 2 },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1, sm: 2 },
        }}
      >
        <Typography
          sx={{ flexGrow: 1, fontSize: { xs: 13.5, sm: 'inherit' }, lineHeight: { xs: 1.45, sm: 1.5 } }}
        >
          We count anonymous visits to understand how people use Recall. Accept to let us remember
          your session across pages. No personal data is sold.{' '}
          <Link href="/privacy">Privacy Policy</Link>
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button variant="outlined" size="small" onClick={decline} sx={{ flex: { xs: 1, sm: 'none' } }}>
            Decline
          </Button>
          <Button variant="contained" size="small" onClick={handleAccept} sx={{ flex: { xs: 1, sm: 'none' } }}>
            Accept
          </Button>
        </Box>
      </Box>
    </Paper>
  )
}
